import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, X, ExternalLink, AlertCircle, Shield, Activity } from "lucide-react";
import { MainLayout } from "@/components/layout/main-layout";

interface BrokerageStatus {
  configured: boolean;
  mode: string;
  features: string[];
}

interface BrokerageStatuses {
  alpaca: BrokerageStatus;
  tradier: BrokerageStatus;
  coinbase: BrokerageStatus;
  kraken: BrokerageStatus;
  interactiveBrokers: BrokerageStatus;
}

interface BrokerageInfo {
  name: string;
  description: string;
  website: string;
  docUrl: string;
  envVars: string[];
  supported: boolean;
  comingSoon?: boolean;
  note?: string;
}

const brokerageInfo: Record<string, BrokerageInfo> = {
  alpaca: {
    name: "Alpaca",
    description: "Commission-free trading API for stocks and crypto. Features paper trading mode.",
    website: "https://alpaca.markets",
    docUrl: "https://docs.alpaca.markets",
    envVars: ["ALPACA_API_KEY", "ALPACA_SECRET_KEY"],
    supported: true,
  },
  tradier: {
    name: "Tradier",
    description: "Professional-grade brokerage API with options trading support.",
    website: "https://tradier.com",
    docUrl: "https://documentation.tradier.com",
    envVars: ["TRADIER_ACCESS_TOKEN", "TRADIER_ACCOUNT_ID"],
    supported: true,
  },
  coinbase: {
    name: "Coinbase",
    description: "Cryptocurrency exchange for buying, selling, and storing digital assets.",
    website: "https://coinbase.com",
    docUrl: "https://docs.cloud.coinbase.com",
    envVars: ["COINBASE_API_KEY", "COINBASE_API_SECRET"],
    supported: false,
    comingSoon: true,
  },
  kraken: {
    name: "Kraken",
    description: "Advanced cryptocurrency trading platform with margin and futures.",
    website: "https://kraken.com",
    docUrl: "https://docs.kraken.com",
    envVars: ["KRAKEN_API_KEY", "KRAKEN_API_SECRET"],
    supported: false,
    comingSoon: true,
  },
  interactiveBrokers: {
    name: "Interactive Brokers",
    description: "Global trading platform for stocks, options, futures, and forex.",
    website: "https://interactivebrokers.com",
    docUrl: "https://interactivebrokers.github.io/tws-api",
    envVars: ["IB_USERNAME", "IB_PASSWORD", "IB_ACCOUNT_ID"],
    supported: false,
    comingSoon: true,
    note: "Requires TWS or IB Gateway",
  },
};

function BrokerageCard({ 
  brokerage, 
  status 
}: { 
  brokerage: string; 
  status: BrokerageStatus;
}) {
  const info = brokerageInfo[brokerage];
  
  return (
    <Card data-testid={`brokerage-card-${brokerage}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {info.name}
            {info.supported ? (
              status.configured ? (
                <Badge variant="default" className="ml-2">
                  <Check className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="ml-2">Not Connected</Badge>
              )
            ) : (
              <Badge variant="outline" className="ml-2">Coming Soon</Badge>
            )}
          </CardTitle>
          {status.configured && (
            <Badge variant="outline" className="text-xs">
              {status.mode}
            </Badge>
          )}
        </div>
        <CardDescription>{info.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {info.note && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted p-2 rounded-md">
            <AlertCircle className="h-4 w-4" />
            {info.note}
          </div>
        )}
        
        <div>
          <h4 className="text-sm font-medium mb-2">Supported Features</h4>
          <div className="flex flex-wrap gap-2">
            {status.features.map((feature) => (
              <Badge key={feature} variant="secondary" className="text-xs">
                {feature}
              </Badge>
            ))}
          </div>
        </div>
        
        {info.supported && (
          <div>
            <h4 className="text-sm font-medium mb-2">Required Environment Variables</h4>
            <div className="space-y-1">
              {info.envVars.map((envVar) => (
                <div 
                  key={envVar} 
                  className="flex items-center gap-2 text-sm font-mono bg-muted px-2 py-1 rounded"
                >
                  {status.configured ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <X className="h-3 w-3 text-red-500" />
                  )}
                  {envVar}
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(info.website, "_blank")}
            data-testid={`button-website-${brokerage}`}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Website
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(info.docUrl, "_blank")}
            data-testid={`button-docs-${brokerage}`}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            API Docs
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BrokerageSettings() {
  const { data: statuses, isLoading } = useQuery<BrokerageStatuses>({
    queryKey: ["/api/brokerages/status"],
  });

  const connectedCount = statuses 
    ? Object.values(statuses).filter(s => s.configured).length 
    : 0;

  return (
    <MainLayout
      title="Brokerage Connections"
      subtitle="Connect your brokerage accounts to enable live trading"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Activity className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{connectedCount}</div>
                <div className="text-sm text-muted-foreground">Connected Brokerages</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">Paper</div>
                <div className="text-sm text-muted-foreground">Trading Mode</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Activity className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">2</div>
                <div className="text-sm text-muted-foreground">Supported Platforms</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-blue-500/50 bg-blue-500/5">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium">Secure API Key Storage</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your API keys are stored as encrypted secrets and never exposed to the frontend. 
                To add or update your brokerage credentials, use the Secrets tab in your Replit project.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Loading brokerage status...
        </div>
      ) : statuses ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.keys(brokerageInfo).map((key) => (
            <BrokerageCard
              key={key}
              brokerage={key}
              status={(statuses as any)[key] || { configured: false, mode: "not_configured", features: [] }}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          Failed to load brokerage status
        </div>
      )}
      </div>
    </MainLayout>
  );
}
